import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { getSupabaseClient } from '../../lib/supabase';
import { 
  Users, 
  Search, 
  Plus, 
  Phone, 
  Calendar, 
  IndianRupee, 
  Clock, 
  ChevronRight, 
  X, 
  RefreshCw,
  ArrowUpRight,
  UserCheck,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { CustomerSupplierForm } from '../retail/CustomerSupplierForm';

export interface CustomerWithServices {
  id: string;
  customer_name: string;
  phone: string;
  email?: string | null;
  balance: number;
  bookings_count: number;
  advance_held: number;
  due_balance: number;
  last_booking_date?: string | null;
  last_booking_status?: string | null;
  bookings: any[];
}

export function ServicesCustomersView({ branchId }: { branchId?: string | null } = {}) {
  const { currentTenantId, activeBranchId } = useAuth();
  const navigate = useNavigate();
  const supabase = getSupabaseClient();
  const effectiveBranchId = branchId || activeBranchId;

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<CustomerWithServices[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithServices | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const loadCustomersData = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      // 1. Fetch retail customers with balance
      let customersList: any[] = [];
      try {
        const { data: balData, error: balErr } = await supabase.rpc('get_customers_with_balance', {
          p_tenant_id: currentTenantId
        });
        if (!balErr && Array.isArray(balData)) {
          customersList = balData;
        }
      } catch (e) {
        console.warn('get_customers_with_balance failed, falling back to direct query', e);
      }

      // If empty or fallback, fetch from retail_customers table directly
      if (customersList.length === 0) {
        const { data: directCust } = await supabase
          .from('retail_customers')
          .select('*')
          .eq('tenant_id', currentTenantId);
        customersList = directCust || [];
      }

      // 2. Fetch service bookings to aggregate per-customer metrics
      let bookingQuery = supabase
        .from('service_bookings')
        .select('*')
        .eq('tenant_id', currentTenantId);

      if (effectiveBranchId) {
        bookingQuery = bookingQuery.eq('branch_id', effectiveBranchId);
      }

      const { data: bookingsData, error: bErr } = await bookingQuery.order('created_at', { ascending: false });

      if (bErr) {
        console.error('Error fetching service bookings for customers:', bErr);
      }

      const allBookings = bookingsData || [];

      // Create lookup maps for fast matching
      const customerMap = new Map<string, CustomerWithServices>();

      // Initialize with existing customers
      customersList.forEach((c: any) => {
        if (c.customer_name === '@@GROUP_DUMMY@@' || c.enable === false) return;
        const phone = (c.contact_no || c.phone || '').trim();
        customerMap.set(c.id, {
          id: c.id,
          customer_name: c.customer_name || 'Unnamed Customer',
          phone: phone,
          email: c.email || null,
          balance: Number(c.current_balance ?? c.balance ?? 0),
          bookings_count: 0,
          advance_held: 0,
          due_balance: 0,
          last_booking_date: null,
          last_booking_status: null,
          bookings: [],
        });
      });

      // Also map phone numbers to customer IDs
      const phoneToId = new Map<string, string>();
      customerMap.forEach((cust, id) => {
        if (cust.phone) {
          const cleanPhone = cust.phone.replace(/\D/g, '').slice(-10);
          if (cleanPhone) phoneToId.set(cleanPhone, id);
        }
      });

      // Process all service bookings
      allBookings.forEach((b: any) => {
        let matchedCustId = b.customer_id;
        const bPhoneClean = (b.customer_phone || '').replace(/\D/g, '').slice(-10);

        if (!matchedCustId && bPhoneClean && phoneToId.has(bPhoneClean)) {
          matchedCustId = phoneToId.get(bPhoneClean);
        }

        // If customer was created only inside service bookings without a retail_customers row
        if (!matchedCustId) {
          const pseudoId = `svc_cust_${b.customer_phone || b.customer_name}`;
          if (!customerMap.has(pseudoId)) {
            customerMap.set(pseudoId, {
              id: pseudoId,
              customer_name: b.customer_name || 'Customer',
              phone: b.customer_phone || '',
              balance: 0,
              bookings_count: 0,
              advance_held: 0,
              due_balance: 0,
              last_booking_date: null,
              last_booking_status: null,
              bookings: [],
            });
            if (bPhoneClean) phoneToId.set(bPhoneClean, pseudoId);
          }
          matchedCustId = pseudoId;
        }

        const targetCust = customerMap.get(matchedCustId);
        if (targetCust) {
          targetCust.bookings.push(b);
          targetCust.bookings_count++;

          // Advance held calculation
          const rec = Number(b.advance_amount) || 0;
          const app = Number(b.advance_applied) || 0;
          const ref = Number(b.advance_refunded) || 0;
          const remainingAdvance = Math.max(0, rec - app - ref);
          if (remainingAdvance > 0 && !['cancelled', 'rejected'].includes(b.status)) {
            targetCust.advance_held += remainingAdvance;
          }

          // Due balance on completed but unpaid jobs
          if (b.status === 'completed' && b.payment_status !== 'paid') {
            const finalP = Number(b.final_price ?? b.quoted_price ?? 0);
            targetCust.due_balance += Math.max(0, finalP - rec);
          }

          // Last booking
          if (!targetCust.last_booking_date || new Date(b.created_at) > new Date(targetCust.last_booking_date)) {
            targetCust.last_booking_date = b.created_at;
            targetCust.last_booking_status = b.status;
          }
        }
      });

      setCustomers(Array.from(customerMap.values()));
    } catch (err: any) {
      console.error('loadCustomersData error:', err);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [supabase, currentTenantId, effectiveBranchId]);

  useEffect(() => {
    loadCustomersData();
  }, [loadCustomersData]);

  const filteredCustomers = customers.filter(c => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      c.customer_name.toLowerCase().includes(q) ||
      c.phone.toLowerCase().includes(q)
    );
  });

  const totalAdvanceHeldAll = customers.reduce((acc, c) => acc + c.advance_held, 0);
  const totalDueAll = customers.reduce((acc, c) => acc + c.due_balance, 0);
  const customersWithBookings = customers.filter(c => c.bookings_count > 0).length;

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 text-primary rounded-xl">
              <Users size={22} />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              Service Customers & Client Ledger
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Customer directory, active service bookings, advance custody, and receivables
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadCustomersData}
            disabled={loading}
            className="text-xs h-9 gap-1.5"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>

          <Button
            size="sm"
            onClick={() => setShowAddForm(true)}
            className="bg-primary hover:bg-primary/90 text-white text-xs h-9 gap-1.5 font-semibold"
          >
            <Plus size={14} />
            <span>Add Customer</span>
          </Button>
        </div>
      </div>

      {/* KPI Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 sm:gap-4">
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
              Total Clients
            </span>
            <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
              {customers.length}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-primary uppercase tracking-wider block">
              With Service Bookings
            </span>
            <div className="text-2xl font-bold text-primary mt-1">
              {customersWithBookings}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-purple-600 uppercase tracking-wider block">
              Advance Held
            </span>
            <div className="text-2xl font-bold text-purple-600 mt-1 font-mono">
              ₹{totalAdvanceHeldAll.toFixed(2)}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-4">
            <span className="text-[11px] font-semibold text-rose-600 uppercase tracking-wider block">
              Total Due (Baaki)
            </span>
            <div className="text-2xl font-bold text-rose-600 mt-1 font-mono">
              ₹{totalDueAll.toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-3 bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by customer name or phone number..."
            className="pl-10 h-10 text-xs bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-xl"
          />
        </div>
      </div>

      {/* Customers Table */}
      <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs overflow-hidden rounded-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50/75 dark:bg-slate-800/50 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-bold">
                <th className="px-5 py-3.5">Customer Name</th>
                <th className="px-5 py-3.5">Phone Number</th>
                <th className="px-5 py-3.5 text-center">Bookings</th>
                <th className="px-5 py-3.5 text-right">Advance Held</th>
                <th className="px-5 py-3.5 text-right">Baaki (Due)</th>
                <th className="px-5 py-3.5">Last Booking</th>
                <th className="px-5 py-3.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-slate-500">
                    No customers matching your search.
                  </td>
                </tr>
              ) : (
                filteredCustomers.map(cust => (
                  <tr
                    key={cust.id}
                    onClick={() => setSelectedCustomer(cust)}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                  >
                    {/* Name */}
                    <td className="px-5 py-4">
                      <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <span>{cust.customer_name}</span>
                        {cust.bookings_count > 0 && (
                          <span className="p-0.5 rounded-full bg-primary/10 text-primary">
                            <UserCheck size={12} />
                          </span>
                        )}
                      </div>
                      {cust.email && (
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">{cust.email}</p>
                      )}
                    </td>

                    {/* Phone */}
                    <td className="px-5 py-4 font-mono text-slate-700 dark:text-slate-300">
                      {cust.phone ? (
                        <div className="flex items-center gap-1.5">
                          <Phone size={11} className="text-slate-400" />
                          <span>{cust.phone}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">No phone</span>
                      )}
                    </td>

                    {/* Bookings Count */}
                    <td className="px-5 py-4 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                        cust.bookings_count > 0 
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300' 
                          : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                      }`}>
                        {cust.bookings_count}
                      </span>
                    </td>

                    {/* Advance Held */}
                    <td className="px-5 py-4 text-right font-mono font-bold">
                      {cust.advance_held > 0 ? (
                        <span className="text-purple-600 dark:text-purple-400">
                          ₹{cust.advance_held.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-slate-400">₹0.00</span>
                      )}
                    </td>

                    {/* Baaki (Due) */}
                    <td className="px-5 py-4 text-right font-mono font-bold">
                      {cust.due_balance > 0 ? (
                        <span className="text-rose-600 dark:text-rose-400">
                          ₹{cust.due_balance.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-emerald-600 dark:text-emerald-400">₹0.00</span>
                      )}
                    </td>

                    {/* Last Booking */}
                    <td className="px-5 py-4 text-slate-600 dark:text-slate-300">
                      {cust.last_booking_date ? (
                        <div>
                          <div>{new Date(cust.last_booking_date).toLocaleDateString()}</div>
                          <span className="text-[10px] uppercase font-bold text-slate-400">
                            {cust.last_booking_status}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic text-[11px]">Never booked</span>
                      )}
                    </td>

                    {/* Action */}
                    <td className="px-5 py-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCustomer(cust);
                        }}
                        className="text-xs h-8 text-primary hover:text-primary/90 gap-1 font-semibold"
                      >
                        <span>History</span>
                        <ChevronRight size={14} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Customer Bookings Drawer */}
      {selectedCustomer && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex justify-end animate-in fade-in duration-150">
          <div className="bg-white dark:bg-slate-900 w-full max-w-lg h-full shadow-2xl flex flex-col border-l border-slate-200 dark:border-slate-800 animate-in slide-in-from-right duration-200 overflow-hidden">
            {/* Header */}
            <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-white">
                  {selectedCustomer.customer_name}
                </h2>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                  {selectedCustomer.phone && <span>{selectedCustomer.phone}</span>}
                  <span>•</span>
                  <span>{selectedCustomer.bookings_count} Bookings</span>
                </div>
              </div>

              <button
                onClick={() => setSelectedCustomer(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200/50"
              >
                <X size={18} />
              </button>
            </div>

            {/* Financial Summary */}
            <div className="p-4 bg-slate-50/50 dark:bg-slate-800/30 border-b border-slate-200 dark:border-slate-800 grid grid-cols-2 gap-3">
              <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                <span className="text-[10px] font-bold uppercase text-purple-600 block">
                  Advance Held
                </span>
                <span className="text-lg font-bold font-mono text-purple-600 mt-0.5 block">
                  ₹{selectedCustomer.advance_held.toFixed(2)}
                </span>
              </div>

              <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                <span className="text-[10px] font-bold uppercase text-rose-600 block">
                  Outstanding Due
                </span>
                <span className="text-lg font-bold font-mono text-rose-600 mt-0.5 block">
                  ₹{selectedCustomer.due_balance.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Bookings List */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                Service Booking History ({selectedCustomer.bookings.length})
              </h3>

              {selectedCustomer.bookings.length === 0 ? (
                <div className="p-8 text-center text-xs text-slate-500 border border-dashed rounded-xl">
                  No bookings found for this customer.
                </div>
              ) : (
                selectedCustomer.bookings.map((b: any) => (
                  <div
                    key={b.id}
                    className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-xs text-slate-900 dark:text-white">
                        {b.booking_number}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        b.status === 'completed' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300' :
                        b.status === 'in_progress' ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300' :
                        'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                      }`}>
                        {b.status}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>{new Date(b.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
                      <span className="font-mono font-bold text-slate-900 dark:text-white text-sm">
                        ₹{b.final_price || b.quoted_price}
                      </span>
                    </div>

                    {b.advance_amount > 0 && (
                      <div className="text-[11px] text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/30 px-2 py-1 rounded-md flex justify-between font-mono">
                        <span>Advance: ₹{b.advance_amount}</span>
                        <span>Mode: {b.advance_mode || 'Cash'}</span>
                      </div>
                    )}

                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedCustomer(null);
                          navigate('/services/booking');
                        }}
                        className="text-[11px] h-7 gap-1"
                      >
                        <span>Open in Bookings</span>
                        <ArrowUpRight size={12} />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex gap-2">
              <Button
                onClick={() => {
                  setSelectedCustomer(null);
                  navigate('/services/booking');
                }}
                className="w-full bg-primary hover:bg-primary/90 text-white text-xs h-9 font-semibold gap-1.5"
              >
                <Plus size={14} />
                <span>Create New Booking for {selectedCustomer.customer_name}</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Add Form Modal (reusing Retail CustomerSupplierForm) */}
      {showAddForm && (
        <CustomerSupplierForm
          isOpen={showAddForm}
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            setShowAddForm(false);
            loadCustomersData();
            toast.success('Customer saved successfully');
          }}
          initialData={{ companyType: 'Customer' }}
        />
      )}
    </div>
  );
}
